import { Controller } from '@hotwired/stimulus'

// Composer template insertion. On <select> change (#preview) it fetches the chosen
// template rendered through the #282 variables engine server-side and shows the
// substituted subject/body plus an unresolved-token hint. On Insert (#insert) it copies
// the previewed text into the existing composer fields — replacing an empty body,
// appending after a blank line to a non-empty one. The picker <div> is a SIBLING of the
// composer form, so #insert resolves the fields from the enclosing form/#compose, not
// from this.element (descendants-only). Preview writes are .textContent, field writes
// are .value — never innerHTML (plain text; no injection into the preview or the fields).
//
// Out-of-order responses are dropped with a monotonic request sequence (segment_preview
// idiom); no AbortController — there is no AbortController precedent in this codebase.
export default class extends Controller {
  static targets = [
    'select',
    'subjectPreview',
    'bodyPreview',
    'unresolved',
    'unresolvedCount',
    'empty'
  ]
  static values = { url: String, conversationId: String }

  connect() {
    this.seq = 0
  }

  // no debounce — a discrete <select> change has nothing to coalesce; keep the seq stale-drop
  async preview() {
    if (this.selectTarget.value === '') {
      // Invalidate any preview fetch still in flight: bump the sequence so a late
      // response is dropped by the `seq !== this.seq` guard below instead of silently
      // re-populating the preview and re-arming Insert after we cleared it.
      ++this.seq
      // Clearing back to the blank option must also drop the cached substituted
      // text, or a subsequent Insert would write the STALE previous template.
      // insert()'s `previewBody === undefined` guard then makes Insert a no-op.
      this.previewBody = undefined
      this.previewSubject = undefined
      this.bodyPreviewTarget.classList.add('d-none')
      this.unresolvedTarget.classList.add('d-none')
      if (this.hasSubjectPreviewTarget) this.subjectPreviewTarget.classList.add('d-none')
      this.emptyTarget.classList.remove('d-none')
      return
    }

    const seq = ++this.seq
    const url =
      `${this.urlValue}?template_id=${encodeURIComponent(this.selectTarget.value)}` +
      (this.conversationIdValue
        ? `&conversation_id=${encodeURIComponent(this.conversationIdValue)}`
        : '')

    let data
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) return
      data = await response.json()
    } catch {
      return
    }
    if (seq !== this.seq) return // a newer request already returned — ignore this stale one

    // Cache the substituted text so #insert writes exactly what was previewed, without
    // re-parsing display chrome (the "Subject: " label below) back out of the DOM.
    this.previewBody = data.body
    this.previewSubject = data.subject

    this.emptyTarget.classList.add('d-none')

    this.bodyPreviewTarget.textContent = data.body
    this.bodyPreviewTarget.classList.remove('d-none')

    if (this.hasSubjectPreviewTarget) {
      this.subjectPreviewTarget.textContent = `Subject: ${data.subject}`
      this.subjectPreviewTarget.classList.remove('d-none')
    }

    const count = data.unresolved.length
    if (count > 0) {
      // Write the count to the inner <span>, NOT the outer <p> — the <p> also
      // holds a sibling bi-braces <i>, and setting textContent on it would
      // destroy that icon (textContent replaces all children). "Unfilled", not
      // "will stay as-is": a known-but-blank token is substituted to EMPTY (not
      // left verbatim), so the reviewer must fill these before sending.
      this.unresolvedCountTarget.textContent = `${count} unfilled placeholder(s) — fill these in`
      this.unresolvedTarget.classList.remove('d-none')
    } else {
      this.unresolvedTarget.classList.add('d-none')
    }
  }

  insert() {
    if (this.previewBody === undefined) return // nothing previewed yet

    const scope = this.element.closest('form, #compose') ?? document
    const bodyField = scope.querySelector('[name="draft[body]"]')
    const subjectField = scope.querySelector('[name="draft[subject]"]')

    if (bodyField) {
      bodyField.value =
        bodyField.value.trim() === ''
          ? this.previewBody
          : `${bodyField.value}\n\n${this.previewBody}`
    }

    if (subjectField) subjectField.value = this.previewSubject
  }
}
